/**
 * useSummaryAnalytics.js
 *
 * Analytics hook for the Assessment Wizard's Screen 12 ("Summary &
 * Review"). Mirrors useMealScheduleAnalytics.js / useWomensHealthAnalytics.js
 * exactly — same GA4 wrapper via react-ga4, same IS_PRODUCTION guard,
 * same dev console.group/table logging.
 *
 * trackGeneratePlanClick fires the exact 'assessment_complete' event name
 * the FSD specifies for Screen 12's Generate Diet Plan button (UC-02),
 * distinct from the generic *_step_next naming every other step uses. It
 * now fires once the /generateDietPlan submission actually succeeds
 * (SummaryStep.api.js), not merely on click — "assessment_complete" should
 * mean the assessment was persisted, not just that the user tried.
 * trackGeneratePlanSubmitFailed covers the case where that submission
 * fails, distinct from trackValidationBlocked (which covers the form
 * being locally incomplete and never reaching the API at all).
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

const send = (eventName, params = {}) => {
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[SummaryAnalytics] ${eventName}`, "color:#6A1B9A;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const useSummaryAnalytics = () => {
  const trackStepView = useCallback((sessionId) => {
    send("summary_step_view", { session_id: sessionId, step: 12 });
  }, []);

  const trackEditSection = useCallback((sessionId, sectionKey) => {
    send("summary_edit_section_click", { session_id: sessionId, section: sectionKey });
  }, []);

  const trackStepPrevious = useCallback((sessionId) => {
    send("summary_step_previous", { session_id: sessionId, step: 12 });
  }, []);

  const trackValidationBlocked = useCallback((sessionId, errors = {}) => {
    send("summary_validation_blocked", {
      session_id: sessionId,
      missing_sections: Object.keys(errors).join(","),
    });
  }, []);

  // FSD Screen 12: "Generate Diet Plan (button) ... logs 'assessment_complete'
  // analytics event." Named exactly as specified, not summary_step_next.
  // Fired only after the /generateDietPlan call succeeds — see file header.
  const trackGeneratePlanClick = useCallback((sessionId) => {
    send("assessment_complete", { session_id: sessionId, step: 12 });
  }, []);

  /**
   * @param {string} sessionId
   * @param {string} [message] - The short, user-presentable error message
   *   (network failure copy, or the API's own errorMessage) — never the
   *   full submitted assessment or any raw field value.
   */
  const trackGeneratePlanSubmitFailed = useCallback((sessionId, message) => {
    send("summary_generate_plan_submit_failed", {
      session_id: sessionId,
      step: 12,
      message,
    });
  }, []);

  return {
    trackStepView,
    trackEditSection,
    trackStepPrevious,
    trackValidationBlocked,
    trackGeneratePlanClick,
    trackGeneratePlanSubmitFailed,
  };
};

export default useSummaryAnalytics;